import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, errorMessage } from '../../lib/api.ts';
import { colourHex } from '../../lib/format.ts';
import { cssVars } from '../../lib/css.ts';
import NewDeckChrome from './NewDeckChrome.tsx';
import { COLOUR_CHIPS, intakeChat, resolveColourWord, useIntakeMessages, useDeckNameCheck } from './shared.tsx';
import type { BlankDeckPayload } from '../../../shared/api.ts';

/** title → colour → creating. There is no terminal phase: a created deck
 *  navigates away, and a failure drops back to 'colour' to try again. */
type BlankPhase = 'title' | 'colour' | 'creating';

/**
 * Blank-start flow: title → colour → POST /api/wizard/blank → editor.
 * The server creates just a cover + thank-you slide deterministically (no
 * planner, no agent, no LLM) — the user builds the deck one slide at a time
 * in the editor via chat and the add-slide gallery.
 */
export default function NewDeckBlank() {
  const navigate = useNavigate();
  const { messages, busyText, setBusyText, say, userSaid } = useIntakeMessages();
  const [phase, setPhase] = useState<BlankPhase>('title');
  const [title, setTitle] = useState('');
  const [convOpen, setConvOpen] = useState(true);
  const checkName = useDeckNameCheck();

  useEffect(() => {
    say(
      'Starting blank: you get a cover and a thank-you slide, then build the deck one slide at a time in the editor. What’s the deck called?',
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function create(nextTitle: string, baseColour: string) {
    setPhase('creating');
    setBusyText('Creating the deck…');
    try {
      const res = await api.post<BlankDeckPayload>('/api/wizard/blank', { title: nextTitle, baseColour });
      setBusyText(null);
      say('Done, opening the editor…');
      navigate(`/editor/${res.slug}`);
    } catch (err) {
      setBusyText(null);
      setPhase('colour');
      say(`Creating the deck failed: ${errorMessage(err)}. Pick a colour to try again.`);
    }
  }

  function handleSend(text: string) {
    const trimmed = text.trim();
    if (!trimmed) return;
    userSaid(trimmed);
    if (phase === 'title') {
      // Check the name here rather than letting the server reject it after the
      // colour step — losing a typed name to a late 409 is the worst moment for
      // it. Same rules as rename/duplicate (codecs/deck-naming.ts).
      const problem = checkName(trimmed);
      if (problem) return say(`${problem} What should the deck be called?`);
      setTitle(trimmed);
      setPhase('colour');
      say(`"${trimmed}", good. Pick a base accent colour (it styles the cover and is the default for new sections).`);
    } else if (phase === 'colour') {
      const c = resolveColourWord(trimmed);
      if (!c) return say('Pick one of the colour chips.');
      create(title, c);
    }
  }

  const chips =
    phase === 'colour'
      ? COLOUR_CHIPS.map((c) => (
          <button
            key={c}
            type="button"
            className="chip"
            style={cssVars({ '--chip-accent': colourHex(c) })}
            onClick={() => handleSend(c)}
          >
            <span className="tool-card__chip-dot" style={{ background: colourHex(c) }} />{' '}
            {c.charAt(0) + c.slice(1).toLowerCase()}
          </button>
        ))
      : null;

  const chat = intakeChat(messages, busyText, handleSend);

  return (
    <NewDeckChrome
      deckName={title || 'New deck'}
      generating={phase === 'creating'}
      summary={null}
      slug={null}
      blank={<p className="muted">A blank deck: cover + thank-you, then one slide at a time.</p>}
      chat={chat}
      commandBarPlaceholder={phase === 'title' ? 'e.g. Quarterly client review' : 'Pick a colour chip…'}
      commandBarDisabled={phase === 'creating'}
      renderExtra={chips ? <div className="conversation__chips">{chips}</div> : null}
      convOpen={convOpen}
      onToggleConv={setConvOpen}
      onDrop={(e) => e.preventDefault()}
    />
  );
}
